/**
 * Global Search Service
 *
 * Searches across all entity types — documents, products, suppliers,
 * components, and checklists — returning unified, relevance-ranked results.
 * Queries run in parallel for speed.
 */

import { supabase } from '@/lib/supabase'
import { profileService } from './profileService'

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

export type SearchResultType = 'document' | 'product' | 'supplier' | 'component' | 'checklist'

export interface GlobalSearchResult {
  id: string
  type: SearchResultType
  name: string
  subtitle: string | null
  score: number
  route: string
}

export interface GlobalSearchOptions {
  types?: SearchResultType[]
  limit?: number
}

// -------------------------------------------------------------------
// Constants
// -------------------------------------------------------------------

const DEFAULT_LIMIT = 20
const MAX_CANDIDATES_PER_TABLE = 50

// -------------------------------------------------------------------
// Keyword extraction (reuses documentSearchService pattern)
// -------------------------------------------------------------------

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
  'should', 'may', 'might', 'can', 'shall', 'to', 'of', 'in', 'for',
  'on', 'with', 'at', 'by', 'from', 'as', 'into', 'through', 'during',
  'before', 'after', 'above', 'below', 'between', 'out', 'off', 'over',
  'under', 'again', 'further', 'then', 'once', 'here', 'there', 'when',
  'where', 'why', 'how', 'all', 'each', 'every', 'both', 'few', 'more',
  'most', 'other', 'some', 'such', 'no', 'nor', 'not', 'only', 'own',
  'same', 'so', 'than', 'too', 'very', 'just', 'because', 'but', 'and',
  'or', 'if', 'while', 'about', 'what', 'which', 'who', 'whom', 'this',
  'that', 'these', 'those', 'am', 'it', 'its', 'i', 'me', 'my', 'we',
  'our', 'you', 'your', 'he', 'him', 'his', 'she', 'her', 'they', 'them',
  'their', 'need', 'get', 'show', 'tell', 'give', 'any', 'much',
])

function extractKeywords(query: string): string[] {
  return query
    .toLowerCase()
    .replace(/[^\w\s-]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 1 && !STOP_WORDS.has(word))
    .filter((word, index, arr) => arr.indexOf(word) === index)
}

/** Check if a string field contains a keyword (case-insensitive). */
function fieldMatch(value: string | null | undefined, keyword: string): boolean {
  return !!value && value.toLowerCase().includes(keyword)
}

/** Truncate a string to a max length, breaking at word boundaries. */
function snippet(text: string | null | undefined, maxLen = 80): string | null {
  if (!text) return null
  if (text.length <= maxLen) return text
  const cut = text.substring(0, maxLen)
  const lastSpace = cut.lastIndexOf(' ')
  return (lastSpace > maxLen * 0.5 ? cut.substring(0, lastSpace) : cut) + '…'
}

// -------------------------------------------------------------------
// Per-table search helpers
// -------------------------------------------------------------------

async function searchDocuments(orgId: string, keywords: string[]): Promise<GlobalSearchResult[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('id, name, description, ai_summary, tags, text_content, type')
    .eq('organization_id', orgId)
    .limit(MAX_CANDIDATES_PER_TABLE)

  if (error || !data) return []

  return data
    .map(doc => {
      let score = 0
      const tags = (doc.tags as string[] | null) ?? []
      for (const kw of keywords) {
        if (fieldMatch(doc.name, kw)) score += 10
        if (tags.some(t => t.toLowerCase().includes(kw))) score += 8
        if (fieldMatch(doc.ai_summary, kw)) score += 5
        if (fieldMatch(doc.description, kw)) score += 4
        if (fieldMatch(doc.text_content, kw)) score += 3
      }
      return {
        id: doc.id,
        type: 'document' as const,
        name: doc.name,
        subtitle: snippet(doc.ai_summary || doc.description) || (doc.type as string | null),
        score,
        route: `/documents/${doc.id}`,
      }
    })
    .filter(r => r.score > 0)
}

async function searchProducts(orgId: string, keywords: string[]): Promise<GlobalSearchResult[]> {
  const { data, error } = await supabase
    .from('products')
    .select('id, name, sku, description, category')
    .eq('organization_id', orgId)
    .limit(MAX_CANDIDATES_PER_TABLE)

  if (error || !data) return []

  return data
    .map(p => {
      let score = 0
      for (const kw of keywords) {
        if (fieldMatch(p.name, kw)) score += 10
        if (fieldMatch(p.sku, kw)) score += 8
        if (fieldMatch(p.category, kw)) score += 5
        if (fieldMatch(p.description, kw)) score += 4
      }
      return {
        id: p.id,
        type: 'product' as const,
        name: p.name,
        subtitle: snippet([p.sku, p.category, p.description].filter(Boolean).join(' · ')),
        score,
        route: `/products/${p.id}`,
      }
    })
    .filter(r => r.score > 0)
}

async function searchSuppliers(orgId: string, keywords: string[]): Promise<GlobalSearchResult[]> {
  const { data, error } = await supabase
    .from('suppliers')
    .select('id, name, category, notes, status')
    .eq('organization_id', orgId)
    .limit(MAX_CANDIDATES_PER_TABLE)

  if (error || !data) return []

  return data
    .map(s => {
      let score = 0
      for (const kw of keywords) {
        if (fieldMatch(s.name, kw)) score += 10
        if (fieldMatch(s.category, kw)) score += 5
        if (fieldMatch(s.notes, kw)) score += 3
        if (fieldMatch(s.status, kw)) score += 2
      }
      return {
        id: s.id,
        type: 'supplier' as const,
        name: s.name,
        subtitle: snippet([s.category, s.status].filter(Boolean).join(' · ')),
        score,
        route: `/suppliers/${s.id}`,
      }
    })
    .filter(r => r.score > 0)
}

async function searchComponents(orgId: string, keywords: string[]): Promise<GlobalSearchResult[]> {
  const { data, error } = await supabase
    .from('components')
    .select('id, name, part_number, description, manufacturer_name, category')
    .eq('organization_id', orgId)
    .limit(MAX_CANDIDATES_PER_TABLE)

  if (error || !data) return []

  return data
    .map(c => {
      let score = 0
      for (const kw of keywords) {
        if (fieldMatch(c.name, kw)) score += 10
        if (fieldMatch(c.part_number, kw)) score += 8
        if (fieldMatch(c.manufacturer_name, kw)) score += 5
        if (fieldMatch(c.category as string | null, kw)) score += 5
        if (fieldMatch(c.description, kw)) score += 4
      }
      return {
        id: c.id,
        type: 'component' as const,
        name: c.name,
        subtitle: snippet([c.part_number, c.manufacturer_name, c.description].filter(Boolean).join(' · ')),
        score,
        route: `/components/${c.id}`,
      }
    })
    .filter(r => r.score > 0)
}

async function searchChecklists(orgId: string, keywords: string[]): Promise<GlobalSearchResult[]> {
  const { data, error } = await supabase
    .from('checklists')
    .select('id, name, type, status, progress')
    .eq('organization_id', orgId)
    .limit(MAX_CANDIDATES_PER_TABLE)

  if (error || !data) return []

  return data
    .map(cl => {
      let score = 0
      for (const kw of keywords) {
        if (fieldMatch(cl.name, kw)) score += 10
        if (fieldMatch(cl.type, kw)) score += 5
        if (fieldMatch(cl.status, kw)) score += 3
      }
      return {
        id: cl.id,
        type: 'checklist' as const,
        name: cl.name,
        subtitle: snippet([cl.type, cl.status, cl.progress != null ? `${cl.progress}% complete` : null].filter(Boolean).join(' · ')),
        score,
        route: `/checklists/${cl.id}`,
      }
    })
    .filter(r => r.score > 0)
}

// -------------------------------------------------------------------
// Dispatcher map
// -------------------------------------------------------------------

const SEARCH_FNS: Record<SearchResultType, (orgId: string, keywords: string[]) => Promise<GlobalSearchResult[]>> = {
  document: searchDocuments,
  product: searchProducts,
  supplier: searchSuppliers,
  component: searchComponents,
  checklist: searchChecklists,
}

// -------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------

export const globalSearchService = {
  /**
   * Search across all (or selected) entity types.
   * Runs queries in parallel, merges results by descending relevance score.
   */
  async search(query: string, options: GlobalSearchOptions = {}): Promise<GlobalSearchResult[]> {
    const trimmed = query.trim()
    if (!trimmed) return []

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const keywords = extractKeywords(trimmed)
    if (keywords.length === 0) return []

    const limit = options.limit ?? DEFAULT_LIMIT
    const types = options.types ?? (['document', 'product', 'supplier', 'component', 'checklist'] as SearchResultType[])

    // Run all selected searches in parallel
    const promises = types.map(t => SEARCH_FNS[t](orgId, keywords))
    const resultArrays = await Promise.all(promises)

    // Merge and sort by score descending
    const merged = resultArrays
      .flat()
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)

    return merged
  },
}
